/**
 * Generic single-order detail fetcher for Postgres-backed tables.
 * @module singleOrderDetailsGenericCallForPostgres
 */

import { API_ENDPOINTS } from "../../../enums/apiUrls";
import { SingleOrderDetailsTableName } from "../../../enums/singleOrderDetailsTableName";
import { getBearerToken } from "../../postCall/bearerToken";

/** Parameters for retrieving a single order's details from a specific Postgres table. */
export interface SingleOrderDetailsGenericCallForPostgresParams {
  order_id: string;
  org_id: string;
  /** Target table/resource to query (e.g. coverage, order, clinical-data). */
  table_name: SingleOrderDetailsTableName;
}

/**
 * Fetches details for a single medical PA order from a specified Postgres table.
 *
 * @typeParam T - Expected shape of the response payload.
 * @param params - Order ID, org ID, and the target table name.
 * @param signal - Optional AbortSignal to cancel the in-flight request.
 * @returns Parsed JSON response cast to type T.
 * @throws Re-throws any network or parsing error after logging.
 */
export const singleOrderDetailsGenericCallForPostgres = async <T>(
  params: SingleOrderDetailsGenericCallForPostgresParams,
  signal?: AbortSignal,
): Promise<T> => {
  try {
    const url = `${API_ENDPOINTS.SINGLE_ORDER_DETAILS_GENERIC_CALL_FOR_POSTGRES}/${params.table_name}`;
    const bearerToken = await getBearerToken();
    const raw = JSON.stringify({
      order_id: params.order_id,
      org_id: params.org_id,
    });
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearerToken}`,
      },
      body: raw,
      ...(signal && { signal }),
    });
    const result: T = await response.json();
    return result;
  } catch (error: any) {
    if (error.name !== "AbortError") {
      console.error(error);
    }
    throw error;
  }
};
